import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { UserAuth } from './AuthContext.jsx';
import { holdPage } from './accessHold.js';
import {
    ACCESS_CHANGED_EVENT, ACCESS_CHECK_EVENT, RECHECK_AFTER_HIDDEN_MS,
    accessChangeToast, compareAccess, createSignalDeduper, orgsChanged, permissionsChanged,
} from './accessChange.js';
import { getEffectivePermissions } from '../services/admin-settings/UserPermissionService.jsx';
import { getUser } from '../services/UserProfileService.jsx';
import { bumpAccessVersion, setEffectivePermissions, updateUserInformation } from '../redux-store/ReduxStore.js';
import { notifyGamesChanged } from '../utils/gamesChanged.js';
import { notifyLifecycleChanged } from '../hooks/useSelectedGameLifecycle.js';

const DEBOUNCE_MS = 400;
/** An unexpected refusal re-checks at most this often - a page that keeps getting 403s must not poll. */
const CHECK_GAP_MS = 15000;

/**
 * Re-reads the user's access in place when it may have changed: the server's `access-changed` signal, a tab coming
 * back after being hidden (a signal sent meanwhile is not replayed), or a request refused out of the blue. Mounted
 * once, by the dashboard layout.
 *
 * In place means: permissions are swapped (never cleared - a null unmounts the page), and only when their version
 * changed; the games, switcher and lifecycle re-read themselves, and `accessVersion` moves so pages holding their own
 * access data re-read it (useAccessVersion). When something may move, the page open at that moment is held first
 * (accessHold.js), so a guard that would now refuse it reports why instead of replacing it.
 *
 * @param {{ onChange?: (kind: 'gained'|'reduced', message: string) => void }} [options]
 */
/**
 * The store's access version: put it in the dependencies of a fetch that reads the user's access (their grants, the
 * org catalogue's accessible / suspended flags, their access requests) so it re-reads after every access change. A
 * silent re-read - keep what is shown until the answer lands, never re-fill a form from it.
 */
export function useAccessVersion() {
    return useSelector(state => state.accessVersion || 0);
}

export default function useAccessRefresh({ onChange } = {}) {
    const { user } = UserAuth();
    const store = useStore();
    const dispatch = useDispatch();
    const location = useLocation();
    const pathRef = useRef(location.pathname);
    const tokenRef = useRef(user?.accessToken);
    const onChangeRef = useRef(onChange);
    pathRef.current = location.pathname;
    tokenRef.current = user?.accessToken;
    onChangeRef.current = onChange;

    useEffect(() => {
        const isNewSignal = createSignalDeduper();
        let timer = null;
        let running = false;
        let again = false;
        // Why the next run happens. A refusal-triggered check only re-reads the rest when access really changed;
        // a server signal or a tab coming back always does (the signal may be about games or lifecycle only).
        let pendingCheckOnly = true;
        let hiddenAt = null;
        let lastCheck = 0;
        let stopped = false;

        const run = async () => {
            const accessToken = tokenRef.current;
            if (stopped || !accessToken) return;
            if (running) {
                again = true;
                return;
            }
            running = true;
            const checkOnly = pendingCheckOnly;
            pendingCheckOnly = true;
            try {
                const before = store.getState().effectivePermissions;
                const beforeOrgs = store.getState().userDetails?.orgs || [];
                const [perms, userData] = await Promise.all([
                    getEffectivePermissions({ accessToken }),
                    getUser({ accessToken }).catch(() => null),
                ]);
                if (stopped) return;
                const permsMoved = !!perms && permissionsChanged(before, perms);
                let orgs = null;
                const states = userData?.user?.orgUserStates;
                if (states) orgs = Object.entries(states).map(([orgId, orgState]) => ({ orgId, orgState }));
                const orgsMoved = !!orgs && orgsChanged(beforeOrgs, orgs);
                // Held only when something may move under the page. A check that finds nothing changed holds
                // nothing: a refusal the user walked into (opening an org they were removed from) is theirs to see,
                // not a change to shield their page from.
                if (!checkOnly || permsMoved || orgsMoved) {
                    holdPage(pathRef.current, store.getState().dashboardContext?.selectedOrgId ?? null);
                }
                if (permsMoved) dispatch(setEffectivePermissions(perms));
                if (orgsMoved) dispatch(updateUserInformation({ orgs }));
                // The games list, the switcher and the selected game's lifecycle each re-read themselves on these;
                // the access version tells every private copy of access data - mounted now or later - it is stale.
                if (!checkOnly || permsMoved || orgsMoved) {
                    dispatch(bumpAccessVersion());
                    notifyGamesChanged();
                    notifyLifecycleChanged();
                }
                const message = perms ? accessChangeToast(compareAccess(before, perms)) : null;
                if (message) onChangeRef.current?.(compareAccess(before, perms), message);
            } catch (e) {
                console.warn('Access refresh failed:', e?.message || e);
            } finally {
                running = false;
                if (again && !stopped) {
                    again = false;
                    schedule(pendingCheckOnly);
                }
            }
        };

        const schedule = (checkOnly = false) => {
            if (!checkOnly) pendingCheckOnly = false;
            clearTimeout(timer);
            timer = setTimeout(run, DEBOUNCE_MS);
        };

        const onSignal = (e) => {
            if (isNewSignal(e?.detail?.id)) schedule();
        };
        const onCheck = () => {
            const now = Date.now();
            if (now - lastCheck < CHECK_GAP_MS) return;
            lastCheck = now;
            schedule(true);
        };
        const onVisibility = () => {
            if (document.hidden) {
                hiddenAt = Date.now();
                return;
            }
            const wasHiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
            hiddenAt = null;
            if (wasHiddenFor >= RECHECK_AFTER_HIDDEN_MS) schedule();
        };

        window.addEventListener(ACCESS_CHANGED_EVENT, onSignal);
        window.addEventListener(ACCESS_CHECK_EVENT, onCheck);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopped = true;
            clearTimeout(timer);
            window.removeEventListener(ACCESS_CHANGED_EVENT, onSignal);
            window.removeEventListener(ACCESS_CHECK_EVENT, onCheck);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [store, dispatch]);
}
